import { create } from 'zustand';
import { DEFAULT_CHANNEL_OPTION, type ChannelOption } from '@/constants/channels';

type ChatState = {
	isOpen: boolean;
	// Per-channel unread counters
	unreadByChannel: Record<string, number>;
	// Per-channel scroll position state
	isAtBottomByChannel: Record<string, boolean>;
	// Per-channel last read timestamp (ms since epoch)
	lastReadAtByChannel: Record<string, number>;
	// Per-channel divider start timestamp (first new msg ts)
	dividerStartAtByChannel: Record<string, number>;
	// Per-channel divider visibility until timestamp (ms since epoch). 0 = hidden.
	dividerUntilByChannel: Record<string, number>;
	// Runtime timers to clear read sessions per channel
	_readTimers: Record<string, number | undefined>;

	selectedChannel: ChannelOption; // channel option
	open: () => void;
	close: () => void;
	toggle: () => void;
	setSelectedChannel: (channel: ChannelOption) => void;

	// New per-channel helpers
	incrementUnreadFor: (channel: string, by?: number) => void;
	resetUnreadFor: (channel: string) => void;
	// Show/hide divider helpers
	startReadSessionFor: (channel: string, durationMs?: number, dividerStartAt?: number) => void;
	clearReadSessionFor: (channel: string) => void;
	markNewFrom: (channel: string, firstNewTimestamp: number) => void;
	setIsAtBottomFor: (channel: string, isAtBottom: boolean) => void;
	markChannelRead: (channel: string) => void;
};

export const useChatStore = create<ChatState>((set, get) => ({
	isOpen: false,
	unreadByChannel: {},
	isAtBottomByChannel: {},
	lastReadAtByChannel: {},
	dividerStartAtByChannel: {},
	dividerUntilByChannel: {},
	_readTimers: {},

	selectedChannel: DEFAULT_CHANNEL_OPTION,
	open: () => set({ isOpen: true }),
	close: () => set({ isOpen: false }),
	toggle: () => set((s) => ({ isOpen: !s.isOpen })),
	setSelectedChannel: (channel: ChannelOption) => set({ selectedChannel: channel }),

	// Per-channel helpers
	incrementUnreadFor: (channel: string, by = 1) => {
		const { unreadByChannel } = get();
		const next = { ...unreadByChannel };
		const updated = (next[channel] ?? 0) + by;
		next[channel] = Math.min(100, Math.max(0, updated));
		set({ unreadByChannel: next });
	},
	resetUnreadFor: (channel: string) => {
		const { unreadByChannel, _readTimers } = get();
		if (_readTimers[channel]) {
			clearTimeout(_readTimers[channel]);
			_readTimers[channel] = undefined;
		}
		const next = { ...unreadByChannel, [channel]: 0 };
		set((s) => ({
			unreadByChannel: next,
			lastReadAtByChannel: { ...s.lastReadAtByChannel, [channel]: Date.now() },
			// Do NOT hide divider here; divider persists until chat closed or channel changed
		}));
	},
	// Start or refresh a visibility window for the NEW divider; clears after durationMs (default 4s)
	startReadSessionFor: (channel: string, durationMs = 4_000, dividerStartAt?: number) => {
		const state = get();
		// Clear any existing timer
		const timers = { ...state._readTimers };
		if (timers[channel]) {
			clearTimeout(timers[channel]);
			timers[channel] = undefined;
		}
		const firstTs = dividerStartAt ?? state.dividerStartAtByChannel[channel] ?? 0;
		const until = Date.now() + Math.max(1, durationMs ?? 10_000);
		const timeoutId = setTimeout(
			() => {
				const t = { ...get()._readTimers };
				t[channel] = undefined;
				const unreadNext = { ...get().unreadByChannel, [channel]: 0 };
				set({
					_readTimers: t,
					unreadByChannel: unreadNext,
					lastReadAtByChannel: { ...get().lastReadAtByChannel, [channel]: Date.now() },
					dividerStartAtByChannel: { ...get().dividerStartAtByChannel, [channel]: 0 },
					dividerUntilByChannel: { ...get().dividerUntilByChannel, [channel]: 0 },
				});
			},
			Math.max(1, durationMs ?? 10_000),
		) as unknown as number;

		set({
			lastReadAtByChannel: { ...state.lastReadAtByChannel, [channel]: Date.now() },
			dividerStartAtByChannel: { ...state.dividerStartAtByChannel, [channel]: firstTs },
			dividerUntilByChannel: { ...state.dividerUntilByChannel, [channel]: until },
			_readTimers: { ...timers, [channel]: timeoutId },
		});
	},
	clearReadSessionFor: (channel: string) => {
		const timers = { ...get()._readTimers };
		if (timers[channel]) {
			clearTimeout(timers[channel]);
			timers[channel] = undefined;
		}
		set({
			_readTimers: timers,
			dividerStartAtByChannel: { ...get().dividerStartAtByChannel, [channel]: 0 },
			dividerUntilByChannel: { ...get().dividerUntilByChannel, [channel]: 0 },
		});
	},
	markNewFrom: (channel: string, firstNewTimestamp: number) => {
		const s = get();
		const currentStart = s.dividerStartAtByChannel[channel] ?? 0;
		const nextStart = currentStart || firstNewTimestamp;
		set({
			dividerStartAtByChannel: { ...s.dividerStartAtByChannel, [channel]: nextStart },
		});
	},
	setIsAtBottomFor: (channel: string, isAtBottom: boolean) => {
		const s = get();
		set({ isAtBottomByChannel: { ...s.isAtBottomByChannel, [channel]: isAtBottom } });
	},
	markChannelRead: (channel: string) => {
		if (!channel) return;
		const { resetUnreadFor, clearReadSessionFor } = get();
		resetUnreadFor(channel);
		clearReadSessionFor(channel);
	},
}));
